/** What the server actually said, out of a response the browser asked for as a Blob.
 *
 * WHY THIS EXISTS. `responseType: 'blob'` applies to the ERROR response too, so a 403 or a 404
 * arrives as a Blob of JSON rather than a parsed object. Without unpacking it, every refusal on a
 * download button degrades to one generic sentence written in the browser — and the server's
 * reason, which is the only part that tells the person what to do next, is thrown away at the very
 * last step.
 *
 * That is not hypothetical here. `GET /field-tickets/{id}/pdf` refuses a ticket that states a
 * price to a caller who may see neither project financials nor sign a ticket, and it says so in
 * one sentence naming the alternative. The screen was printing "Failed to download PDF".
 *
 * `Blob.text()` is not available everywhere — notably not in the jsdom build the tests run under,
 * where its absence silently produced exactly that generic message. FileReader is the fallback that
 * works in both, and a failure to read returns null so the caller keeps its own default rather than
 * rendering an empty error.
 *
 * NOTE: `src/modules/timekeeping/pages/MasterTimesheet.jsx` carries the original, file-local copy
 * of this pair. This module is where it belongs; folding that one in is a separate change with its
 * own tests to re-run.
 */

/** Anything a server calls a "detail", as a STRING that is safe to render.
 *
 * THE BUG THIS EXISTS FOR: a FastAPI **422** does not return a string. `detail` is a LIST OF DICTS
 * describing which field failed validation. Rendering that straight into JSX raises React error
 * #31 — "objects are not valid as a React child" — which is not a caught error but a RENDER
 * failure, so it unmounts the whole screen behind a generic "failed to load".
 *
 * An error path that can crash the screen is worse than the error it was reporting. Every branch
 * here ends at a string.
 */
export function asMessage(detail) {
  if (detail === null || detail === undefined) return ''
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    // FastAPI validation shape: [{loc: [...], msg: "...", type: "..."}]
    const parts = detail
      .map((d) => (typeof d === 'string' ? d : (d && d.msg) || ''))
      .filter(Boolean)
    return parts.length ? parts.join('; ') : 'The request was rejected as invalid.'
  }
  if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail)
  return String(detail)
}

/** The `detail` out of an error body that arrived as a Blob, or null if it cannot be read. */
export async function readBlobDetail(blob) {
  let raw = null
  try {
    if (typeof blob.text === 'function') {
      raw = await blob.text()
    } else if (typeof FileReader !== 'undefined') {
      raw = await new Promise((resolve) => {
        const fr = new FileReader()
        fr.onload = () => resolve(String(fr.result || ''))
        fr.onerror = () => resolve(null)
        fr.readAsText(blob)
      })
    }
    return raw ? (asMessage(JSON.parse(raw)?.detail) || null) : null
  } catch {
    return null
  }
}

/** The best sentence available for an axios failure, whatever shape the body arrived in.
 *
 * Handles the three cases a download button actually meets: a Blob body (because the request asked
 * for one), an ordinary parsed JSON body, and no body at all.
 */
export async function errorDetail(err, fallback) {
  const body = err?.response?.data
  if (body && typeof Blob !== 'undefined' && body instanceof Blob) {
    const detail = await readBlobDetail(body)
    if (detail) return detail
  } else if (body && typeof body === 'object') {
    const detail = asMessage(body.detail)
    if (detail) return detail
  }
  return fallback
}
